import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { Form, Formik } from "formik";
import { useHistory } from "react-router-dom";

import { ModalFooter, ModalBody } from "application/modal";
import { Button, BtnType, Label, TextBox, TextArea } from "modules/primitives";
import { useModelPopup } from "../../../common/hooks";
import { clearNewProject, projectNew } from "modules/projects/project/project.action";
import { newFordProject, defaultModelKeys, newFordProjectValidationStep1 } from "./constant";
import FordNewProjectStep1 from "./step1";
import { companyGetTopics } from "modules/companies/companyMain/companyMain.action";
import LoadingComponent from "modules/primitives/loading/loading.component";
import { projectStagesTemplatesGet } from "modules/projects/newProject/stepOne/stepOne.actions";
import styles from "modules/projects/newProject/stepOne/styles.module.scss";
import NewProjectAccess from "../newProjectSharedComponents/newProjectAccess";
import NewProjectTeam from "../newProjectSharedComponents/newProjectTeam";
import NewProjectEvaluation from "../newProjectSharedComponents/newProjectEvaluation";
import { shouldUpdateCompanyProjectTasks } from "modules/companies/company/company.action";
import { httpGet } from "../../httpCall";
import { getPlantsAndAreas } from "modules/admin/adminPlantsAndAreas/adminPlantsAndAreas.actions";

const FordNewProjectModal = ({ data, instance }) => {
  const formRef = useRef(null);
  const [showLoading, setShowLoading] = useState(false);
  const [proposalData, setProposalData] = useState(null);
  const [model, setModel] = useState(newFordProject);
  const { enqueueSnackbar } = useSnackbar();

  const popup = useModelPopup();
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    adminPlantsAndAreasReducer: { plantsAndAreas },
    councilReducer: { selectedCouncil },
    authReducer: { session },
    stepOneReducer: { stageTemplates },
    projectsReducer: {
      projectReducer: { newProjectObject },
    },
    ideaReducer: { ideaPlant },
  } = useSelector((state) => state);

  useEffect(() => {
    if (!plantsAndAreas.length) {
      dispatch(
        getPlantsAndAreas({
          enqueueSnackbar,
        }),
      );
    }
  }, [plantsAndAreas]);

  useEffect(() => {
    httpGet({
      call: 'ford/project_field_templates'
    }).subscribe(res => {
      if (res.response) {
        const proposal_for_implementation = res.response.proposal_for_implementation.data.map(elem => ({
          id: elem.id,
          ...elem.attributes
        }));
        const proposal_for_prototype = res.response.proposal_for_prototype.data.map(elem => ({
          id: elem.id,
          ...elem.attributes
        }));

        setProposalData({
          proposal_for_implementation,
          proposal_for_prototype
        })
      }
    })
  }, []);

  useEffect(() => {
    if (newProjectObject) {
      const projObj = JSON.parse(localStorage.getItem("new_project_object"));

      if (projObj.redirect_approval_task_id) {
        history.push(`/projects/${projObj.id}/task?tab=all_task&taskId=${projObj.redirect_approval_task_id}&gatewayTask=true`);
      } else {
        history.push(`/projects/${projObj && projObj.id}/overview`);
      }

      enqueueSnackbar("Project created with success", {
        variant: "success",
      });
      popup.hide();
      dispatch(shouldUpdateCompanyProjectTasks());
      dispatch(clearNewProject());
    }

    return () => localStorage.removeItem("new_project");
  }, [newProjectObject]);

  useEffect(() => {
    if (!stageTemplates?.length && selectedCouncil) {
      dispatch(
        projectStagesTemplatesGet({
          council_id: selectedCouncil.id,
          enqueueSnackbar,
        })
      );
    }
  }, [selectedCouncil, stageTemplates]);

  useEffect(() => {
    if (!selectedCouncil) {
      return;
    }

    dispatch(
      companyGetTopics({
        councilId: selectedCouncil.id,
        enqueueSnackbar,
      }),
    );
  }, [selectedCouncil]);

  const getCustomFieldValueByName = (source, name) => {
    const getSource = source.find(elem => elem.section_name === name);

    if (getSource) {
      if (getSource.section_type === 'dropdown') {
        return getSource.section_values;
      }

      return getSource.section_value;
    }

    return null
  }

  useEffect(() => {
    const customFields = data?.ideas_descriptions || [];
    const getProblemTemplate = customFields.find(
      (t) => t.section_name === "Problem Trying to Solve?"
    );
    const description = instance === 'company'
      ? ''
      : getProblemTemplate
        ? getProblemTemplate.section_value
        : data.ideas_descriptions[0]?.section_value || '';

    const otherProps = {
      description,
      selected_member_ids: data?.ideas_members?.map((member) => member.user_id),
      selected_company_ids: data?.ideas_related_companies?.map(c => c.company.id),
      name: data.name,
      fps_category: getCustomFieldValueByName(customFields, 'FPS Category'),
      problem_statement: getCustomFieldValueByName(customFields, 'Problem Statement'),
      potential_benefits: getCustomFieldValueByName(customFields, 'Potential Benefits'),
      skills_team: getCustomFieldValueByName(customFields, 'Skills Team'),
      where_did_this_idea_come_from: getCustomFieldValueByName(customFields, 'Where did this Idea come from?'),
      themes: data.themes?.map(t => ({...t, id: t.theme_id})) || [],
      selectedPlant: instance === 'idea' && ideaPlant ? {
        ...ideaPlant,
        selectedAreas: ideaPlant?.areas?.map(area => ({
          area_id: area.id,
          area_name: area.attributes.name,
        })),
      } : null,
      project_id: data.original_project?.id || null,
      isReplication: !!data?.original_project,
      topic_ids: data?.resource_topics?.map(t => t.topic_id) || [],
      similar_projects: data.projects.filter(p => p.similar).map(p => ({ ...p, id: p.project_id })),
    }

    if (stageTemplates?.length) {
      const stage_template_id = stageTemplates
        .filter((template) => template.default_template)[0]?.id;

      setModel((prev) => ({
        ...prev,
        ...otherProps,
        stage_template_id,
      }));
    } else {
      setModel((prev) => ({
        ...prev,
        ...otherProps,
      }));
    }
  }, [stageTemplates, data]);

  const handleDismiss = () => {
    popup.hide();
  };

  const hasTemplateOptions = useMemo(() => {
    return stageTemplates?.length > 0
  }, [stageTemplates]);

  const makeEuropeMFGPayload = () => {
    const values = formRef.current.values;
    const pattern = /-/;
    const customFieldProps = {};
    const simpleFields = {};

    Object.keys(values).forEach(key => {
      // is custom field
      if (pattern.test(key)) {
        customFieldProps[key] = values[key];
      } else {
        simpleFields[key] = values[key];
      }
    });

    const filteredMembers = simpleFields.selected_member_ids?.filter(id => id !== session.id) || [];
    const isIdeaInstance = instance === 'idea';
    let fordCustomProps = {};

    if (isIdeaInstance) {
      fordCustomProps = {
        select_all_members: simpleFields.select_all_members,
        select_all_companies: simpleFields.select_all_companies,
        selected_member_ids: filteredMembers,
        selected_company_ids: simpleFields.selected_company_ids,
      }
    }

    const addedCompany = {
      added_by_id: session.id,
      company_id: data.id,
    };

    const getPossibleReplications = () => {
      if (!simpleFields.possible_replications) {
        return []
      }

      return simpleFields.possible_replications.map(replication => ({
        state: replication.state,
        plant_id: replication.id,
        areas_id: replication.areas.map(area => area.id),
      }))
    }

    const payload = {
      ford_custom_stuff: {
        ...fordCustomProps,
        instruction: simpleFields.instruction,
        project_id: values.isReplication ? simpleFields.project_id || '' : null,
        possible_replications: getPossibleReplications(),
        plant_id: simpleFields.selectedPlant?.id,
        area_ids: simpleFields.selectedPlant?.selectedAreas?.map(area => area.area_id) || [],
        themes: simpleFields.themes?.map(t => t.id) || [],
        next_stage_change: simpleFields.next_stage_change?.date || '',
        investment: simpleFields.investment,
        project_stage: simpleFields.project_stage,
        custom_project_fields: [
          {
            field_name: 'Skills Team',
            field_value: simpleFields.skills_team?.map(el => el?.value || el) || [],
          },
          {
            field_name: 'Where did this Idea come from?',
            field_value: simpleFields.where_did_this_idea_come_from?.map(el => el?.value || el) || [],
          },
          {
            field_name: 'Problem Statement',
            field_value: simpleFields.problem_statement
          },
          {
            field_name: 'Potential Benefits',
            field_value: simpleFields.potential_benefits,
          },
          {
            field_name: 'FPS Category',
            field_value: simpleFields.fps_category?.map(el => el?.value || el) || [],
          }
        ],
      },
      members: instance === 'company'
        ? []
        : filteredMembers,
      name: simpleFields.name,
      description: simpleFields.description,
      visibility: simpleFields.visibility,
      stage_template_id: {
        val: simpleFields.stage_template_id
      },
      councilId: selectedCouncil.id,
      active: true,
      userId: session.id,
      idea_ids: isIdeaInstance ? [data.id] : null,
      project_companies: instance === 'company' ? [addedCompany] : [],
      companies: instance === 'company'
        ? [addedCompany]
        : simpleFields.selected_company_ids?.map(c => ({
          added_by_id: session.id,
          company_id: c,
        })) || [],
      enqueueSnackbar,
      similar_projects: values.similar_projects?.map(p => ({related_project_id: p.id} )),
      project_owners: values.project_owners?.map(o => o.user_id || o.id),
      from_idea: instance === 'idea',
    };

    dispatch(projectNew(payload));
  }

  const makeSimpleProject = () => {
    const otherProps = Object.keys(defaultModelKeys).reduce((acc, key) => {
      return {
        ...acc,
        [key]: formRef.current.values[key] || defaultModelKeys[key],
      }
    }, {});
    const addedCompany = {
      added_by_id: session.id,
      company_id: data.id,
    };
    const payload = {
      ...otherProps,
      councilId: selectedCouncil.id,
      active: true,
      userId: session.id,
      enqueueSnackbar,
      idea_ids: instance === 'idea' ? [data.id] : null,
      idea_id: instance === 'idea' ? data.id : null,
      project_companies: instance === 'company' ? [addedCompany] : null,
      companies: instance === 'company' ? [addedCompany] : null,
      stage_template_id: {
        val: otherProps.stage_template_id
      },
    };

    dispatch(projectNew(payload));
  }

  const isEuropeMFG = useCallback((values) => {
    const getStage = stageTemplates?.find(s => s.id === values?.stage_template_id);
    return getStage?.name === 'Europe MFG Innovation' || getStage?.name === 'US MFG Innovation';
  }, [stageTemplates]);

  const handleCreate = useCallback(() => {
    const isItEuropeTemplate = isEuropeMFG(formRef.current.values);

    if (isItEuropeTemplate && instance === 'idea') {
      if (!formRef.current.values.selectedPlant?.id) {
        enqueueSnackbar("Please select plant", {
          variant: "error",
        });

        return;
      }

      if (!formRef.current.isValid) {
        return;
      }
    }

    if (!stageTemplates) {
      enqueueSnackbar("Please wait for Evaluation Templates to be loaded.", {
        variant: "error",
      });
      return;
    }

    if (!hasTemplateOptions) {
      enqueueSnackbar(
        "Please ask an Admin to set a Project template as the default.",
        {
          variant: "error",
        }
      );
    }

    setShowLoading(true);

    if (isItEuropeTemplate) {
      makeEuropeMFGPayload();
      return;
    }

    makeSimpleProject()
  }, [
    isEuropeMFG,
    stageTemplates,
    hasTemplateOptions,
    data,
    instance,
    proposalData,
    session
  ]);

  const isStuffDisabled = useMemo(() => {
    return (session?.council_role === "standard" ||
        session?.council_role === "viewer") &&
      !session?.theme_investment_access
  }, [session]);

  return (
    <div>
      <Formik
        innerRef={formRef}
        initialValues={model}
        enableReinitialize
        validationSchema={newFordProjectValidationStep1}
        onSubmit={() => {}}
      >
        {(formikprops) => {
          const isItEuropeTemplate = isEuropeMFG(formikprops.values);

          return (
            <Form id="newFordProject">
              <ModalBody>
                {
                  !formikprops.values?.stage_template_id?.length ? (
                    <LoadingComponent customText="Getting Templates..." />
                  ) : (
                    <>
                      {
                        isItEuropeTemplate ? (
                          <div style={{ height: '80vh' }}>
                            <FordNewProjectStep1
                              formikprops={formikprops}
                              data={data}
                              proposalData={proposalData}
                              instance={instance}
                            />
                          </div>
                        ) : (
                          <>
                            <div className={styles.formElement}>
                              <Label>Project name</Label>
                              <TextBox
                                type="text"
                                name="name"
                                placeholder="Project name"
                                formProps={formikprops}
                                className="w-100"
                              />
                            </div>
                            <div className="mb-3 w-100">
                              <NewProjectAccess
                                formikprops={formikprops}
                                isDisabled={isStuffDisabled}
                              />
                            </div>
                            {formikprops.values.visibility === "team" && (
                              <div className="mb-3 w-100">
                                <NewProjectTeam setFieldValue={formikprops.setFieldValue} />
                              </div>
                            )}
                            <div className="mb-3 w-100">
                              <NewProjectEvaluation
                                formikprops={formikprops}
                                isDisabled={isStuffDisabled}
                              />
                            </div>
                            <div className="w-100">
                              <Label>Problem Statement</Label>
                              <TextArea
                                type="text"
                                name="description"
                                placeholder="Describe the problem you are trying to solve, the project goal, or description."
                                className="w-100"
                                formProps={formikprops}
                                rows={8}
                              />
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </ModalBody>
              <ModalFooter>
                {
                  showLoading ? (
                    <LoadingComponent customText="Preparing new project..." />
                  ) : (
                    <>
                      <Button btn={BtnType.OUTLINE} onClick={handleDismiss}>
                        Cancel
                      </Button>
                      <Button
                        btn={BtnType.REGULAR}
                        className="ml-2"
                        type="submit"
                        onClick={handleCreate}
                        disabled={formikprops.values.selectedPlant?.areas?.length ? !formikprops.values.selectedPlant?.selectedAreas?.length : false}
                      >
                        Create
                      </Button>
                    </>
                  )
                }
              </ModalFooter>
            </Form>
          )
        }}
      </Formik>
    </div>
  );
};

export default FordNewProjectModal;
